import { ChartLocation, RectOption, isCollide, rotateTextSize } from '../../common/utils/helper';
import { markerAnimate, appendChildElement, getVisiblePoints } from '../../common/utils/helper';
import { getLabelText, convertHexToColor, calculateRect, textElement, colorNameToHex, animateTextElement } from '../../common/utils/helper';
import { Size, measureText, TextOption, Rect } from '@syncfusion/ej2-svg-base';
import { textRender } from '../../common/model/constants';
import { createTemplate, getFontStyle, getElement, measureElementRect, templateAnimate, withIn, withInBounds } from '../../common/utils/helper';
import { createElement, getValue, extend, initializeTelemetryFeature } from '@syncfusion/ej2-base';
import { getPoint, isRotatedRectIntersect } from '../../common/utils/helper';
/**
 * The `DataLabel` module is used to render data labels for data points.
 */
export class DataLabel {
    /**
     * Constructor for the data label module.
     *
     * @private
     */
    constructor(chart) {
        this.errorHeight = 0;
        /**
         * Stores the resolved label position selected during automatic
         * collision resolution.
         *
         * @private
         */
        this.actualPosition = 'Middle';
        /** @private */
        this.dataLabelRectCollection = {};
        initializeTelemetryFeature('DataLabel', 'Charts');
        this.chart = chart;
    }
    initPrivateVariables(series, marker) {
        let transform = '';
        let clipPath = '';
        const isBubbleCanvasMode = this.chart.enableCanvas && series.type === 'Bubble' && marker.dataLabel.visible;
        const render = isBubbleCanvasMode ? this.chart.svgRenderer : series.chart.renderer;
        const index = (series.index === undefined) ? series.category : series.index;
        if (series.chart.chartAreaType === 'Cartesian') {
            transform = 'translate(' + series.clipRect.x + ',' + (series.clipRect.y) + ')';
            clipPath = 'url(#' + this.chart.element.id + '_ChartSeriesClipRect_' + index + ')';
        }
        if ((marker.dataLabel.visible && !this.chart.enableCanvas) || isBubbleCanvasMode) {
            series.shapeElement = render.createGroup({
                'id': this.chart.element.id + 'ShapeGroup' + index,
                'transform': transform,
                'clip-path': 'url(#' + this.chart.element.id + '_ChartSeriesClipRect_' + index + ')'
            });
            series.textElement = render.createGroup({
                'id': this.chart.element.id + 'TextGroup' + index,
                'transform': transform,
                'clip-path': clipPath
            });
            series.textElement.setAttribute('aria-hidden', 'true');
        }
        this.markerHeight = ((series.type === 'Scatter' || marker.visible)) ? (marker.height / 2) : 0;
        this.commonId = this.chart.element.id + '_Series_' + index + '_Point_';
        this.calculateErrorHeight(series, series.marker.dataLabel.position);
        this.chartBackground = this.chart.chartArea.background === 'transparent' ?
            this.chart.background || this.chart.themeStyle.background : this.chart.chartArea.background;
    }
    calculateErrorHeight(series, position) {
        if (!series.errorBar.visible) {
            return null;
        }
        else if (series.errorBar.visible && this.chart.chartAreaType !== 'PolarRadar') {
            const direction = series.errorBar.direction;
            const positiveHeight = this.chart.errorBarModule.positiveHeight;
            const negativeHeight = this.chart.errorBarModule.negativeHeight;
            if (this.isRectSeries(series)) {
                if (position === 'Top' || position === 'Auto') {
                    if (direction === 'Both' || direction === 'Minus') {
                        this.errorHeight = negativeHeight;
                    }
                    else {
                        this.errorHeight = 0;
                    }
                }
                if (position === 'Outer' || position === 'Auto') {
                    if (direction === 'Both' || direction === 'Plus') {
                        this.errorHeight = positiveHeight;
                    }
                    else {
                        this.errorHeight = 0;
                    }
                }
            }
            else {
                if (position === 'Top' || position === 'Outer' || position === 'Auto') {
                    if ((direction === 'Both' || direction === 'Plus') && (!series.chart.isTransposed)) {
                        this.errorHeight = positiveHeight;
                    }
                    else {
                        this.errorHeight = 0;
                    }
                }
                if (position === 'Bottom' || position === 'Auto') {
                    if (direction === 'Both' || direction === 'Minus') {
                        this.errorHeight = negativeHeight;
                    }
                    else {
                        this.errorHeight = 0;
                    }
                }
            }
        }
        else {
            this.errorHeight = 0;
        }
    }
    isRectSeries(series) {
        return series.isRectSeries || series.type === 'RangeArea' || series.type === 'SplineRangeArea' || series.type === 'RangeStepArea';
    }
    /**
     * Render the data label for series.
     *
     * @param {Series} series - The series to render.
     * @param {Chart} chart - The parent chart.
     * @param {DataLabelSettingsModel} dataLabel - The settings for data labels.
     * @returns {void}
     * @private
     */
    render(series, chart, dataLabel) {
        // initialize the private variable
        this.initPrivateVariables(series, series.marker);
        this.inverted = chart.requireInvertedAxis;
        this.yAxisInversed = series.yAxis.isAxisInverse;
        const templateId = chart.element.id + '_Series_' +
            (series.index === undefined ? series.category : series.index) + '_DataLabelCollections';
        const element = createElement('div', {
            id: templateId
        });
        const visiblePoints = getVisiblePoints(series);
        const secondaryElement = getElement(chart.element.id + '_Secondary_Element');
        const isBubbleCanvasMode = chart.enableCanvas && series.type === 'Bubble' && dataLabel.visible;
        // Data label point iteration started
        if (series.visible) {
            for (let i = 0; i < visiblePoints.length; i++) {
                this.renderDataLabel(series, visiblePoints[i], element, dataLabel);
            }
        }
        if (isBubbleCanvasMode) {
            let labelSvg = getElement(chart.element.id + '_datalabel_svg');
            if (!labelSvg) {
                labelSvg = chart.svgRenderer.createSvg({
                    id: chart.element.id + '_datalabel_svg',
                    width: chart.availableSize.width,
                    height: chart.availableSize.height
                });
                labelSvg.style.cssText = 'position:absolute; display:block; pointer-events:none;';
                secondaryElement.appendChild(labelSvg);
            }
            if (series.shapeElement && series.shapeElement.parentNode !== labelSvg) {
                labelSvg.appendChild(series.shapeElement);
            }
            if (series.textElement && series.textElement.parentNode !== labelSvg) {
                labelSvg.appendChild(series.textElement);
            }
            if (element.childElementCount) {
                appendChildElement(chart.enableCanvas, secondaryElement, element, chart.redraw, false, 'x', 'y', null, '', false, false, null, chart.duration);
            }
        }
        else if (element.childElementCount) {
            if (!chart.enableCanvas) {
                appendChildElement(chart.enableCanvas, getElement(chart.element.id + '_Secondary_Element'), element, chart.redraw, false, 'x', 'y', null, '', false, false, null, chart.duration);
            }
            else {
                getElement(chart.element.id + '_Secondary_Element').appendChild(element);
            }
        }
    }
    renderDataLabel(series, point, element, dataLabel) {
        if (!dataLabel.showZero && ((point.y === 0) || (point.y === 0 && series.emptyPointSettings.mode === 'Zero'))) {
            return null;
        }
        this.margin = dataLabel.margin;
        let labelText = [];
        let labelLength;
        let xPos;
        let yPos;
        let xValue;
        let yValue;
        let degree;
        let rectCenterX;
        let rectCenterY;
        let labelLocation = { x: 0, y: 0 };
        let textSize;
        const clip = series.clipRect;
        let shapeRect;
        let isDataLabelOverlap = false;
        const dataLabelElement = [];
        let startLocation;
        dataLabel.enableRotation = dataLabel.labelIntersectAction === 'Rotate90' ? true : dataLabel.enableRotation;
        const angle = degree = dataLabel.labelIntersectAction === 'Rotate90' ? 90 : dataLabel.angle;
        const border = { width: dataLabel.border.width, color: dataLabel.border.color };
        const argsFont = (extend({}, getValue('properties', dataLabel.font), null, true));
        const isBubbleCanvasMode = this.chart.enableCanvas && series.type === 'Bubble';
        if ((point.symbolLocations.length && point.symbolLocations[0]) ||
            (series.type === 'BoxAndWhisker' && point.regions.length)) {
            labelText = point.text !== null ? getLabelText(point, series, this.chart) : [];
            labelLength = labelText.length;
            for (let i = 0; i < labelLength; i++) {
                const argsData = {
                    cancel: false, name: textRender, series: series,
                    point: point, text: labelText[i], border: border,
                    color: dataLabel.fill, template: dataLabel.template, font: argsFont, location: labelLocation,
                    textSize: measureText(labelText[i], dataLabel.font, this.chart.themeStyle.datalabelFont)
                };
                this.chart.trigger(textRender, argsData);
                if (!argsData.cancel) {
                    this.fontBackground = argsData.color;
                    this.isDataLabelShape(argsData);
                    this.markerHeight = series.type === 'Bubble' ? (point.regions[0].height / 2) : this.markerHeight;
                    if (argsData.template !== null) {
                        this.createDataLabelTemplate(element, series, dataLabel, point, argsData, i, this.chart.redraw, angle);
                    }
                    else {
                        // Split label text on newline characters to support multi-line data labels
                        const labelLines = typeof argsData.text === 'string'
                            ? argsData.text.split(/\r\n|\n/)
                            : [argsData.text];
                        const isMultiLine = labelLines.length > 1;
                        if (dataLabel.enableRotation && isMultiLine) {
                            let maximumWidth = 0;
                            let totalHeight = 0;
                            for (const line of labelLines) {
                                const lineSize = rotateTextSize(dataLabel.font, line, angle, this.chart, this.chart.themeStyle.datalabelFont);
                                maximumWidth = Math.max(maximumWidth, lineSize.width);
                                totalHeight += lineSize.height;
                            }
                            textSize = new Size(maximumWidth, totalHeight);
                        }
                        else if (dataLabel.enableRotation) {
                            textSize = rotateTextSize(dataLabel.font, labelLines[0], angle, this.chart, this.chart.themeStyle.datalabelFont);
                        }
                        else if (isMultiLine) {
                            let maximumWidth = 0;
                            let totalHeight = 0;
                            for (const line of labelLines) {
                                const lineSize = measureText(line, dataLabel.font, this.chart.themeStyle.datalabelFont);
                                maximumWidth = Math.max(maximumWidth, lineSize.width);
                                totalHeight += lineSize.height;
                            }
                            textSize = new Size(maximumWidth, totalHeight);
                        }
                        else {
                            textSize = measureText(argsData.text, dataLabel.font, this.chart.themeStyle.datalabelFont);
                        }
                        const rect = this.calculateTextPosition(point, series, textSize, dataLabel, i, angle);
                        let unRotatedText;
                        if (isMultiLine) {
                            let maximumWidth = 0;
                            let totalHeight = 0;
                            for (const line of labelLines) {
                                const lineSize = measureText(line, dataLabel.font, this.chart.themeStyle.datalabelFont);
                                maximumWidth = Math.max(maximumWidth, lineSize.width);
                                totalHeight += lineSize.height;
                            }
                            unRotatedText = new Size(maximumWidth, totalHeight);
                        }
                        else {
                            unRotatedText = measureText(argsData.text, dataLabel.font, this.chart.themeStyle.datalabelFont);
                        }
                        rectCenterX = rect.x + (rect.width / 2);
                        rectCenterY = rect.y + (rect.height / 2);
                        const rotateRect = dataLabel.enableRotation
                            ? new Rect((rectCenterX - (unRotatedText.width / 2) - this.margin.left), (rectCenterY - (unRotatedText.height / 2) - this.margin.top), unRotatedText.width + this.margin.left + this.margin.right, unRotatedText.height + this.margin.top + this.margin.bottom)
                            : rect;
                        const actualRect = new Rect(rect.x + clip.x, rect.y + clip.y, rect.width, rect.height);
                        //let notOverlapping: boolean;
                        if (dataLabel.enableRotation) {
                            const rectCoordinates = this.getRectanglePoints(actualRect);
                            rectCenterX = rotateRect.x + (rotateRect.width / 2);
                            rectCenterY = rotateRect.y + (rotateRect.height / 2);
                            isDataLabelOverlap = (dataLabel.labelIntersectAction === 'Rotate90' || angle === -90 || angle === 90) ?
                                false : this.isDataLabelOverlapWithChartBound(rectCoordinates, this.chart, { x: 0, y: 0, width: 0, height: 0 });
                            if (!isDataLabelOverlap) {
                                this.chart.rotatedDataLabelCollections.push(rectCoordinates);
                                const currentPointIndex = this.chart.rotatedDataLabelCollections.length - 1;
                                for (let index = currentPointIndex; index >= 0; index--) {
                                    if (this.chart.rotatedDataLabelCollections[currentPointIndex] &&
                                        this.chart.rotatedDataLabelCollections[index - 1] &&
                                        isRotatedRectIntersect(this.chart.rotatedDataLabelCollections[currentPointIndex], this.chart.rotatedDataLabelCollections[index - 1])) {
                                        isDataLabelOverlap = true;
                                        this.chart.rotatedDataLabelCollections[currentPointIndex] = null;
                                        break;
                                    }
                                }
                            }
                        }
                        else {
                            isDataLabelOverlap = isCollide(rect, this.chart.dataLabelCollections, clip);
                        }
                        if ((!isDataLabelOverlap || dataLabel.labelIntersectAction === 'None')) {
                            const dataLabelShapeElement = getElement(this.commonId + point.index + '_TextShape_' + i);
                            if (dataLabelShapeElement) {
                                startLocation = { x: +dataLabelShapeElement.getAttribute('x'), y: +dataLabelShapeElement.getAttribute('y') };
                            }
                            this.chart.dataLabelCollections.push(actualRect);
                            if (this.isShape) {
                                shapeRect = (isBubbleCanvasMode ? this.chart.svgRenderer : this.chart.renderer).drawRectangle(new RectOption(this.commonId + point.index + '_TextShape_' + i, argsData.color, argsData.border, dataLabel.opacity, rotateRect, dataLabel.rx, dataLabel.ry, '', dataLabel.border.dashArray), new Int32Array([clip.x, clip.y]));
                                if (series.shapeElement) {
                                    if (isBubbleCanvasMode) {
                                        series.shapeElement.appendChild(shapeRect);
                                    }
                                    else {
                                        appendChildElement(this.chart.enableCanvas, series.shapeElement, shapeRect, this.chart.redraw, true, 'x', 'y', startLocation);
                                    }
                                }
                            }
                            // Checking the font color
                            const backgroundColor = this.fontBackground === 'transparent' ? ((this.chart.theme.indexOf('Dark') > -1 || this.chart.theme.indexOf('HighContrast') > -1) ? 'black' : 'white') : this.fontBackground;
                            const rgbValue = convertHexToColor(colorNameToHex(backgroundColor));
                            const contrast = Math.round((rgbValue.r * 299 + rgbValue.g * 587 + rgbValue.b * 114) / 1000);
                            xPos = (rotateRect.x + this.margin.left + unRotatedText.width / 2) + labelLocation.x;
                            const firstLineHeight = measureText(labelLines[0], dataLabel.font, this.chart.themeStyle.datalabelFont).height;
                            yPos = (rotateRect.y + this.margin.top + firstLineHeight * 3 / 4) + labelLocation.y;
                            labelLocation = { x: 0, y: 0 };
                            if (angle !== 0 && dataLabel.enableRotation) {
                                // xValue = xPos - (dataLabel.margin.left) / 2 + (dataLabel.margin.right / 2);
                                xValue = rectCenterX;
                                //yValue = yPos - (dataLabel.margin.top) / 2 - (textSize.height / dataLabel.margin.top) +
                                // (dataLabel.margin.bottom) / 2;
                                yValue = rectCenterY;
                                degree = (angle > 360) ? angle - 360 : (angle < -360) ? angle + 360 : angle;
                            }
                            else {
                                degree = 0;
                                xValue = rect.x;
                                yValue = rect.y;
                                xPos -= this.chart.chartAreaType === 'Cartesian' && xPos + (textSize.width / 2) > clip.width ? (!this.chart.requireInvertedAxis && xPos > clip.width) ? 0 : (xPos + textSize.width / 2) - clip.width : 0;
                                yPos -= (yPos + textSize.height > clip.y + clip.height && !(series.type.indexOf('Bar') > -1)) ? (yPos + textSize.height) - (clip.y + clip.height) : 0;
                            }
                            const textAnchor = 'middle';
                            let oldText;
                            if (this.chart.redraw && document.getElementById(this.commonId + point.index + '_Text_' + i)) {
                                oldText = document.getElementById(this.commonId + point.index + '_Text_' + i).textContent;
                            }
                            if (argsData.text) {
                                const textElem = textElement(isBubbleCanvasMode ? this.chart.svgRenderer : this.chart.renderer, new TextOption(this.commonId + ((series.removedPointIndex !== null && series.removedPointIndex <= point.index) ? (point.index + 1) : point.index) + '_Text_' + i, xPos, yPos, textAnchor, isMultiLine ? labelLines : argsData.text, 'rotate(' + degree + ',' + (xValue) + ',' + (yValue) + ')', 'auto', degree), argsData.font, argsData.font.color || (this.chart.theme === 'Bootstrap5' ? '#212529' : this.chart.theme === 'Bootstrap5Dark' ? '#DEE2E6' : ((contrast >= 128 || series.type === 'Hilo' || series.type === 'HiloOpenClose') ?
                                    this.chart.theme.indexOf('Tailwind3') > -1 ? '#111827' : 'black' : this.chart.theme.indexOf('Tailwind3') > -1 ? '#FFFFFF' : 'white')), series.textElement, false, this.chart.redraw, true, false, series.chart.duration, series.clipRect, null, null, isBubbleCanvasMode ? false : this.chart.enableCanvas, isMultiLine, this.chart.themeStyle.datalabelFont, new ChartLocation(xValue, yValue));
                                dataLabelElement.push(textElem);
                            }
                            if (this.isShape && dataLabel.enableRotation) {
                                shapeRect.setAttribute('transform', 'rotate(' + angle + ', ' + xValue + ', ' + yValue + ')');
                            }
                            if (this.chart.stackLabels.visible && series.type.indexOf('Stacking') > -1) {
                                this.dataLabelRectCollection = !this.dataLabelRectCollection ? {}
                                    : this.dataLabelRectCollection;
                                this.dataLabelRectCollection[this.commonId + ((series.removedPointIndex !== null && series.removedPointIndex <= point.index) ? (point.index + 1) : point.index) + '_Text_' + i] = actualRect;
                                this.dataLabelRectCollection[this.commonId + point.index + '_TextShape_' + i] = actualRect;
                            }
                            if (series.removedPointIndex !== null && series.removedPointIndex <= point.index) {
                                series.textElement.lastChild.id = this.commonId + point.index + '_Text_' + i;
                            }
                            if (this.chart.redraw && oldText !== argsData.text) {
                                animateTextElement(series.textElement.querySelector('#' + this.commonId + point.index + '_Text_' + i), this.chart.duration, parseFloat(oldText), parseFloat(argsData.text), series.marker.dataLabel.format || series.yAxis.labelFormat);
                            }
                        }
                        else if (getElement(this.commonId + point.index + '_Text_0') && series.chart.redraw && series.currentData) {
                            getElement(this.commonId + point.index + '_Text_0').remove();
                        }
                    }
                }
            }
        }
        return dataLabelElement;
    }
    /**
     * Renders the stack labels for the chart.
     *
     * This method is responsible for displaying cumulative total values on stacked chart segments.
     *
     * @returns {void}
     */
    renderStackLabels() {
        const stackLabelGroup = this.chart.renderer.createGroup({ id: `${this.chart.element.id}_StackLabelGroup` });
        if (!this.chart.enableCanvas && stackLabelGroup) {
            this.chart.seriesElements.appendChild(stackLabelGroup);
        }
        let positivePoints = {};
        let negativePoints = {};
        const groupingValues = [];
        let keys = [];
        let stackLabelIndex = 0;
        if (this.chart.visibleSeries && this.chart.visibleSeries.length > 0) {
            for (let i = 0; i < this.chart.visibleSeries.length; i++) {
                keys = Object.keys(groupingValues);
                const series = this.chart.visibleSeries[i];
                if (!groupingValues[series.stackingGroup]) {
                    groupingValues[series.stackingGroup] = [];
                    groupingValues[series.stackingGroup].push(series);
                }
                else if (groupingValues[series.stackingGroup] !== undefined) {
                    groupingValues[series.stackingGroup].push(series);
                }
            }
            if (keys[0] !== '') {
                for (let groupIndex = 0; groupIndex < keys.length; groupIndex++) {
                    positivePoints = {};
                    negativePoints = {};
                    const seriesInGroup = groupingValues[keys[groupIndex]];
                    if (!seriesInGroup || seriesInGroup.length === 0) {
                        continue;
                    }
                    // Iterate series in reverse to preserve stacking order
                    for (let si = seriesInGroup.length - 1; si >= 0; si--) {
                        const series = seriesInGroup[si];
                        if (!series || !series.visible) {
                            continue;
                        }
                        if (!this.chart.enableCanvas && series.animation.enable && this.chart.animateSeries) {
                            stackLabelGroup.setAttribute('visibility', 'hidden');
                        }
                        if (series.points && series.points.length > 0) {
                            for (let pointIndex = 0; pointIndex < series.points.length; pointIndex++) {
                                const point = series.points[pointIndex];
                                const pointXValueAsKey = String(point.x);
                                if (!positivePoints[pointXValueAsKey] && series.stackedValues.endValues[pointIndex] > 0
                                    && point.visible) {
                                    positivePoints[pointXValueAsKey] = point;
                                }
                                if (!negativePoints[pointXValueAsKey] && series.stackedValues.endValues[pointIndex] < 0
                                    && point.visible) {
                                    negativePoints[pointXValueAsKey] = point;
                                }
                            }
                        }
                    }
                    stackLabelIndex = this.calculateStackLabel(positivePoints, negativePoints, stackLabelGroup, stackLabelIndex);
                }
            }
            else {
                for (let seriesIndex = this.chart.visibleSeries.length - 1; seriesIndex >= 0; seriesIndex--) {
                    const series = this.chart.visibleSeries[seriesIndex];
                    if (!this.chart.enableCanvas && series.animation.enable && this.chart.animateSeries) {
                        stackLabelGroup.setAttribute('visibility', 'hidden');
                    }
                    if (series.visible && series.points && series.points.length > 0) {
                        for (let pointIndex = 0; pointIndex < series.points.length; pointIndex++) {
                            const point = series.points[pointIndex];
                            const pointXValueAsKey = String(point.x);
                            if (!positivePoints[pointXValueAsKey] && series.stackedValues.endValues[pointIndex] > 0
                                && point.visible) {
                                positivePoints[pointXValueAsKey] = point;
                            }
                            if (!negativePoints[pointXValueAsKey] && series.stackedValues.endValues[pointIndex] < 0
                                && point.visible) {
                                negativePoints[pointXValueAsKey] = point;
                            }
                        }
                    }
                }
                this.calculateStackLabel(positivePoints, negativePoints, stackLabelGroup, stackLabelIndex);
            }
        }
    }
    /**
     * This method is responsible for positioning the cumulative sum of stacking column series.
     *
     * @param {Object} positivePoints - The positive points of the stacking series.
     * @param {Object} negativePoints - The negative points of the stacking series.
     * @param {Element} stackLabelGroup - The stack label group element.
     * @param {number} stackLabelIndex - The index of the stack label.
     * @returns {number} - The number of stack labels.
     */
    calculateStackLabel(positivePoints, negativePoints, stackLabelGroup, stackLabelIndex) {
        [positivePoints, negativePoints].forEach((points, index) => {
            if (points) {
                let totalValue = 0;
                let currentPoint;
                Object.keys(points).forEach((pointXValueAsKey) => {
                    const positiveValue = points[pointXValueAsKey].series
                        .stackedValues.endValues[points[pointXValueAsKey].index];
                    const negativeValue = negativePoints[pointXValueAsKey] ?
                        negativePoints[pointXValueAsKey].series.stackedValues.
                            endValues[negativePoints[pointXValueAsKey].index] : 0;
                    if (index === 0) {
                        // Handle positive points
                        totalValue = positiveValue + negativeValue;
                        currentPoint = points[pointXValueAsKey];
                    }
                    else if (!positivePoints[pointXValueAsKey]) {
                        // Handle negative points only if no corresponding positive point
                        totalValue = positiveValue;
                        currentPoint = points[pointXValueAsKey];
                    }
                    if (currentPoint && currentPoint.symbolLocations[0] &&
                        withInBounds(currentPoint.symbolLocations[0].x + currentPoint.series.clipRect.x, currentPoint.symbolLocations[0].y + currentPoint.series.clipRect.y, currentPoint.series.clipRect)) {
                        const series = currentPoint.series;
                        const symbolLocation = currentPoint.symbolLocations[0];
                        const labelFormat = this.chart.stackLabels.format;
                        let stackLabeltext = (totalValue % 1 === 0)
                            ? totalValue.toFixed(0)
                            : (totalValue.toFixed(2).slice(-1) === '0' ? totalValue.toFixed(1) : totalValue.toFixed(2));
                        if (labelFormat) {
                            const customLabelFormat = labelFormat.match('{value}') !== null;
                            stackLabeltext = customLabelFormat
                                ? labelFormat.replace('{value}', stackLabeltext.toString())
                                : this.chart.intl.getNumberFormat({
                                    format: labelFormat,
                                    useGrouping: this.chart.useGroupingSeparator
                                })(totalValue);
                        }
                        const argsStackLabelFont = (extend({}, getValue('properties', this.chart.stackLabels.font), null, true));
                        const argsData = {
                            cancel: false, name: textRender, series: series,
                            point: null, text: stackLabeltext, border: this.chart.stackLabels.border,
                            color: this.chart.stackLabels.fill, template: null, font: argsStackLabelFont, location: null,
                            textSize: measureText(stackLabeltext, this.chart.stackLabels.font, this.chart.themeStyle.datalabelFont)
                        };
                        this.chart.trigger(textRender, argsData);
                        if (!argsData.cancel) {
                            const textSize = measureText(argsData.text, argsData.font, this.chart.themeStyle.datalabelFont);
                            // Define padding to maintain a consistent gap from the symbol location values
                            const padding = 10;
                            if (this.chartBackground === undefined) {
                                this.chartBackground = this.chart.chartArea.background === 'transparent' ?
                                    this.chart.background || this.chart.themeStyle.background : this.chart.chartArea.background;
                            }
                            const backgroundColor = argsData.color === 'transparent' && this.chartBackground === 'transparent' ? ((this.chart.theme.indexOf('Dark') > -1 || this.chart.theme.indexOf('HighContrast') > -1) ? 'black' : 'white') : this.chart.stackLabels.fill !== 'transparent' ? this.chart.stackLabels.fill : this.chartBackground;
                            const rgbValue = convertHexToColor(colorNameToHex(backgroundColor));
                            const contrast = Math.round((rgbValue.r * 299 + rgbValue.g * 587 + rgbValue.b * 114) / 1000);
                            const alignmentValue = textSize.width + argsData.border.width
                                + this.chart.stackLabels.margin.left + this.chart.stackLabels.margin.right - padding / 2;
                            const yOffset = this.chart.requireInvertedAxis ? padding / 2 :
                                (this.chart.primaryYAxis.isInversed ? (index === 0 ? (textSize.height + padding / 2) : -padding)
                                    : (index === 0 ? -padding : (textSize.height + padding / 2)));
                            let xOffset = this.chart.requireInvertedAxis ?
                                ((this.chart.primaryYAxis.isInversed ? (index === 0 ? -(padding + textSize.width / 2) :
                                    (padding + textSize.width / 2)) : (index === 0 ? (padding + textSize.width / 2) :
                                    -(padding + textSize.width / 2)))) : 0;
                            xOffset += argsData.font.textAlignment === 'Far' ? alignmentValue :
                                (argsData.font.textAlignment === 'Near' ? -alignmentValue : 0);
                            let xPosition = Math.max(series.clipRect.x + textSize.width, Math.min(xOffset + series.clipRect.x + symbolLocation.x, series.clipRect.x
                                + series.clipRect.width - textSize.width));
                            let yPosition = Math.max(series.clipRect.y + textSize.height, Math.min(yOffset + series.clipRect.y + symbolLocation.y -
                                ((this.chart.stackLabels.angle > 0 && !this.chart.requireInvertedAxis) ? textSize.width / 2 : 0), series.clipRect.y + series.clipRect.height - textSize.height));
                            if (this.chart.enableCanvas) {
                                xPosition -= series.clipRect.x;
                                yPosition -= series.clipRect.y;
                            }
                            const rect = new Rect(xPosition - textSize.width / 2 - this.chart.stackLabels.margin.left, yPosition - textSize.height - this.chart.stackLabels.margin.top, textSize.width + (this.chart.stackLabels.margin.left + this.chart.stackLabels.margin.right), textSize.height + padding / 2 + (this.chart.stackLabels.margin.top + this.chart.stackLabels.margin.bottom));
                            const shapeRect = this.chart.renderer.drawRectangle(new RectOption(`${this.chart.element.id}StackLabel_TextShape_${stackLabelIndex}`, argsData.color, argsData.border, null, rect, this.chart.stackLabels.rx, this.chart.stackLabels.ry, '', null), new Int32Array([symbolLocation.x, symbolLocation.y]));
                            shapeRect.setAttribute('transform', `rotate(${this.chart.stackLabels.angle}, ${xPosition}, ${yPosition})`);
                            appendChildElement(this.chart.enableCanvas, stackLabelGroup, shapeRect, this.chart.redraw);
                            textElement(this.chart.renderer, new TextOption(`${this.chart.element.id}_StackLabel_${stackLabelIndex}`, xPosition, yPosition, 'middle', argsData.text, `rotate(${this.chart.stackLabels.angle}, ${xPosition}, ${yPosition})`, 'auto', this.chart.stackLabels.angle), argsData.font, (argsData.font.color || (this.chart.theme === 'Bootstrap5' ? '#212529' : this.chart.theme === 'Bootstrap5Dark' ? '#DEE2E6' : ((contrast >= 128) ?
                                this.chart.theme.indexOf('Tailwind3') > -1 ? '#111827' : 'black' : this.chart.theme.indexOf('Tailwind3') > -1 ? '#FFFFFF' : 'white'))), stackLabelGroup, null, this.chart.redraw, true, null, this.chart.duration, series.clipRect, null, null, this.chart.enableCanvas, null, this.chart.themeStyle.datalabelFont, null);
                            if (series.type === 'StackingLine' || series.type === 'StackingArea') {
                                document.querySelectorAll(`[id^="${this.chart.element.id}_Series_${series.index}_Point_${currentPoint.index}_Text_"], 
                                [id^="${this.chart.element.id}_Series_${series.index}_Point_${currentPoint.index}_TextShape_"]`).forEach((element) => {
                                    if (element.id) {
                                        element.style.visibility = 'hidden';
                                        element.setAttribute('data-collide', 'true');
                                    }
                                });
                            }
                            for (const dataLabelID in this.dataLabelRectCollection) {
                                if (Object.prototype.hasOwnProperty.call(this.dataLabelRectCollection, dataLabelID)) {
                                    const dataLabelRect = this.dataLabelRectCollection[dataLabelID];
                                    if (dataLabelRect) {
                                        const isCollided = isCollide(rect, [dataLabelRect], { x: 0, y: 0, height: 0, width: 0 });
                                        if (isCollided) {
                                            const dataLabelElement = document.getElementById(dataLabelID);
                                            if (dataLabelElement) {
                                                dataLabelElement.style.visibility = 'hidden';
                                                dataLabelElement.setAttribute('data-collide', 'true');
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    stackLabelIndex++;
                });
            }
        });
        return stackLabelIndex;
    }
    /**
     * Retrieves the points of a rectangle.
     *
     * @param {Rect} rect - The rectangle whose points are to be retrieved.
     * @returns {ChartLocation[]} - The points of the rectangle.
     */
    getRectanglePoints(rect) {
        const loc1 = new ChartLocation(rect.x, rect.y);
        const loc2 = new ChartLocation(rect.x + rect.width, rect.y);
        const loc3 = new ChartLocation(rect.x + rect.width, rect.y + rect.height);
        const loc4 = new ChartLocation(rect.x, rect.y + rect.height);
        return [loc1, loc2, loc3, loc4];
    }
    isDataLabelOverlapWithChartBound(rectCoordinates, chart, clip) {
        for (let index = 0; index < rectCoordinates.length; index++) {
            if (!withInBounds(rectCoordinates[index].x + clip.x, rectCoordinates[index].y + clip.y, chart.initialClipRect)) {
                return true;
            }
        }
        return false;
    }
    /**
     * Creates a template for data labels.
     *
     * @param {HTMLElement} parentElement - The parent element to which the template will be appended.
     * @param {Series} series - The series associated with the data label.
     * @param {DataLabelSettingsModel} dataLabel - The settings for the data label.
     * @param {Points} point - The data point to which the data label is associated.
     * @param {ITextRenderEventArgs} data - The event data associated with rendering the data label.
     * @param {number} labelIndex - The index of the data label.
     * @param {boolean} redraw - Specifies whether to redraw the template.
     * @param {number} angle - Specifies the angle of rotation.
     * @returns {void}
     */
    createDataLabelTemplate(parentElement, series, dataLabel, point, data, labelIndex, redraw, angle) {
        this.margin = { left: 0, right: 0, bottom: 0, top: 0 };
        const clip = series.clipRect;
        const childElement = createTemplate(createElement('div', {
            id: this.chart.element.id + '_Series_' + (series.index === undefined ? series.category : series.index) + '_DataLabel_'
                + point.index + (labelIndex ? ('_' + labelIndex) : ''),
            styles: 'position: absolute;background-color:' + data.color + ';' +
                getFontStyle(dataLabel.font, this.chart.themeStyle.datalabelFont) + ';border:' + data.border.width + 'px solid ' + data.border.color + ';'
        }), point.index, (this.chart.enableHtmlSanitizer ? this.chart.sanitize(data.template) : data.template), this.chart, point, series, this.chart.element.id + '_DataLabel', labelIndex);
        this.calculateTemplateLabelSize(parentElement, childElement, point, series, dataLabel, labelIndex, clip, redraw, null, angle);
    }
    calculateTemplateLabelSize(parentElement, childElement, point, series, dataLabel, labelIndex, clip, redraw, isReactCallback, angle) {
        const elementRect = measureElementRect(childElement, redraw, isReactCallback);
        const rect = this.calculateTextPosition(point, series, { width: elementRect.width, height: elementRect.height }, dataLabel, labelIndex, angle);
        const clipWidth = 0;
        const clipHeight = 0;
        let isOverlap = false;
        if (isReactCallback) {
            isOverlap = (elementRect.width === 0 || elementRect.height === 0); // To check the data label already overlap before react callback call
            // clipWidth = ((series.clipRect.x + rect.x) + elementRect.width) > parentElement.clientWidth ?
            //     (parentElement.clientWidth - (series.clipRect.x + rect.x)) : 0;
            // clipHeight = (series.points.length - 1 === point.index) ? elementRect.height / 2 : 0;
        }
        childElement.style.left = ((this.chart.chartAreaType === 'PolarRadar' ? 0 : series.clipRect.x) + rect.x - clipWidth) + 'px';
        childElement.style.top = ((this.chart.chartAreaType === 'PolarRadar' ? 0 : series.clipRect.y) + rect.y + clipHeight) + 'px';
        const vAxis = series.chart.requireInvertedAxis ? series.xAxis : series.yAxis;
        const hAxis = series.chart.requireInvertedAxis ? series.yAxis : series.xAxis;
        if (childElement.childElementCount && !isOverlap && (!isCollide(rect, this.chart.dataLabelCollections, clip) ||
            dataLabel.labelIntersectAction === 'None') && (series.seriesType !== 'XY' || point.yValue === undefined ||
            withIn(point.yValue, series.yAxis.visibleRange) || (series.type.indexOf('Stacking') > -1) ||
            (series.type.indexOf('100') > -1 && withIn(series.stackedValues.endValues[point.index], series.yAxis.visibleRange))) &&
            withIn(point.xValue, series.xAxis.visibleRange) && parseFloat(childElement.style.top) >= vAxis.rect.y &&
            parseFloat(childElement.style.left) >= hAxis.rect.x &&
            parseFloat(childElement.style.top) <= vAxis.rect.y + vAxis.rect.height &&
            parseFloat(childElement.style.left) <= hAxis.rect.x + hAxis.rect.width) {
            this.chart.dataLabelCollections.push(new Rect(rect.x + clip.x, rect.y + clip.y, rect.width, rect.height));
            appendChildElement(this.chart.enableCanvas, parentElement, childElement, redraw, true, 'left', 'top');
            if (series.animation.enable && this.chart.animateSeries && !this.chart.enableCanvas) {
                this.doDataLabelAnimation(series, childElement);
            }
            else if (this.chart.enableCanvas) {
                parentElement.appendChild(childElement);
            }
        }
    }
    calculateTextPosition(point, series, textSize, dataLabel, labelIndex, angle) {
        let labelRegion = labelIndex > 1 ? (series.type === 'Candle') ? point.regions[1] : point.regions[0] : point.regions[0];
        if (labelIndex > 1 && series.type === 'HiloOpenClose') {
            labelRegion = (labelIndex === 2) ? point.regions[1] : point.regions[2];
        }
        let location;
        location = this.getLabelLocation(point, series, textSize, labelIndex);
        const padding = 5;
        const clipRect = series.clipRect;
        // calculating alignment
        if (!this.chart.requireInvertedAxis || !this.isRectSeries(series) || series.type === 'BoxAndWhisker') {
            this.locationX = location.x;
            const alignmentValue = textSize.height + (this.borderWidth * 2) + this.markerHeight +
                this.margin.bottom + this.margin.top + padding;
            location.x = (dataLabel.position === 'Auto') ? location.x :
                this.calculateAlignment(alignmentValue, location.x, dataLabel.alignment, this.isRectSeries(series) ? point.yValue < 0 : false);
            // calculating position
            location.y = (!this.isRectSeries(series) || series.type === 'BoxAndWhisker') ?
                this.calculatePathPosition(location.y, dataLabel.position, series, point, textSize, labelIndex) :
                this.calculateRectPosition(location.y, labelRegion, point.yValue < 0 !== this.yAxisInversed, dataLabel.position, series, textSize, labelIndex, point);
            if (this.isRectSeries(series) && this.chart.chartAreaType === 'PolarRadar') {
                location = this.calculatePolarRectPosition(location, dataLabel.position, series, point, textSize, labelIndex, dataLabel.alignment, alignmentValue);
            }
        }
        else {
            this.locationY = location.y;
            const alignmentValue = textSize.width + this.borderWidth + this.margin.left + this.margin.right - padding;
            location.x = dataLabel.position === 'Auto' ? location.x :
                this.calculateAlignment(alignmentValue, location.x, dataLabel.alignment, point.yValue < 0);
            location.x = this.calculateRectPosition(location.x, labelRegion, point.yValue < 0 !== this.yAxisInversed, dataLabel.position, series, textSize, labelIndex, point);
        }
        const rect = calculateRect(location, textSize, this.margin);
        // Checking the condition whether data Label has been exist the clip rect
        if (!(dataLabel.enableRotation === true && (angle !== 0)) &&
            !((rect.y > (clipRect.y + clipRect.height)) || (rect.x > (clipRect.x + clipRect.width)) ||
                (rect.x + rect.width < 0) || (rect.y + rect.height < 0))) {
            rect.x = rect.x < 0 ? (series.type === 'StackingColumn' && !this.inverted ? 0 : padding) : rect.x;
            rect.y = (rect.y < 0 && !this.chart.requireInvertedAxis) && !(dataLabel.labelIntersectAction === 'None') ? padding : rect.y;
            rect.x -= (rect.x + rect.width) > (clipRect.x + clipRect.width) ? (rect.x + rect.width)
                - (clipRect.x + clipRect.width) + padding : 0;
            rect.y -= (rect.y + rect.height) > (clipRect.y + clipRect.height) ? (rect.y + rect.height)
                - (clipRect.y + clipRect.height) + padding : 0;
            this.fontBackground = this.fontBackground === 'transparent' ? this.chartBackground : this.fontBackground;
        }
        let dataLabelOutRegion;
        if (this.inverted && series.isRectSeries && (rect.x + rect.width > labelRegion.x + labelRegion.width)) {
            dataLabelOutRegion = true;
        }
        this.fontBackground = dataLabelOutRegion ? this.chartBackground : this.fontBackground;
        return rect;
    }
    // Calculation label location for polar column draw types
    calculatePolarRectPosition(location, position, series, point, size, labelIndex, alignment, alignmentValue) {
        const padding = 5;
        let columnRadius;
        const chartWidth = this.chart.availableSize.width;
        const alignmentSign = (alignment === 'Center') ? 0 : (alignment === 'Far' ? 1 : -1);
        const angle = (point.regionData.startAngle - 0.5 * Math.PI) + (point.regionData.endAngle - point.regionData.startAngle) / 2;
        if (labelIndex === 0) {
            columnRadius = point.regionData.radius < point.regionData.innerRadius ? point.regionData.innerRadius
                : point.regionData.radius;
        }
        else {
            columnRadius = point.regionData.radius > point.regionData.innerRadius ? point.regionData.innerRadius
                : point.regionData.radius;
        }
        this.fontBackground = this.fontBackground === 'transparent' ? this.chartBackground : this.fontBackground;
        if (series.drawType.indexOf('Stacking') > -1) {
            position = position === 'Outer' ? 'Top' : position;
        }
        else if (series.drawType.indexOf('Range') > -1) {
            position = (position === 'Outer' || position === 'Top') ? position : 'Auto';
        }
        if (position === 'Outer') {
            columnRadius = labelIndex === 0 ? columnRadius + 2 * padding + this.markerHeight :
                columnRadius - 2 * padding - this.markerHeight;
        }
        else if (position === 'Middle') {
            columnRadius = columnRadius / 2 + padding;
            if (series.drawType === 'StackingColumn') {
                columnRadius = point.regionData.innerRadius + ((point.regionData.radius - point.regionData.innerRadius) / 2)
                    + padding - (size.height / 2);
            }
        }
        else if (position === 'Top') {
            columnRadius = labelIndex === 0 ? columnRadius - 2 * padding - this.markerHeight :
                columnRadius + 2 * padding + this.markerHeight;
        }
        else if (position === 'Bottom') {
            columnRadius = 2 * padding;
            columnRadius += (series.drawType === 'StackingColumn') ? (point.regionData.innerRadius + this.markerHeight) : 0;
        }
        else {
            if (labelIndex === 0) {
                columnRadius = columnRadius >= series.chart.radius ? columnRadius - padding :
                    series.drawType === 'StackingColumn' ? columnRadius - 2 * padding : columnRadius + 2 * padding;
            }
            else {
                columnRadius = columnRadius >= series.chart.radius ? columnRadius + padding : columnRadius - 2 * padding;
            }
        }
        columnRadius += (alignmentValue * alignmentSign);
        location.x = series.clipRect.width / 2 + series.clipRect.x + columnRadius * Math.cos(angle);
        // To change x location based on text anchor for column and stackingcolumn chart
        if (series.drawType === 'StackingColumn') {
            location.x = location.x < chartWidth / 2 ? location.x + size.width / 2 :
                (location.x > chartWidth / 2 ? location.x - size.width / 2 : location.x);
        }
        else if (series.drawType === 'Column') {
            location.x = location.x < chartWidth / 2 ? location.x - size.width / 2 :
                (location.x > chartWidth / 2 ? location.x + size.width / 2 : location.x);
        }
        location.y = series.clipRect.height / 2 + series.clipRect.y + columnRadius * Math.sin(angle);
        return location;
    }
    /**
     * Gets the location for the data label.
     *
     * @param {Points} point - The data point associated with the label.
     * @param {Series} series - The series associated with the data label.
     * @param {Size} textSize - The size of the text to be displayed in the data label.
     * @param {number} labelIndex - The index of the data label.
     * @returns {ChartLocation} - The location for the data label.
     */
    getLabelLocation(point, series, textSize, labelIndex) {
        let location = new ChartLocation(0, 0);
        let labelRegion = (series.type === 'Candle' && labelIndex > 1) ? point.regions[1] : point.regions[0];
        if (series.type === 'HiloOpenClose') {
            labelRegion = (labelIndex === 2) ? point.regions[1] : point.regions[2];
        }
        const xAxis = series.xAxis;
        const yAxis = series.yAxis;
        const isInverted = series.chart.requireInvertedAxis;
        if (series.type === 'BoxAndWhisker') {
            this.markerHeight = 0;
            switch (labelIndex) {
                case 0:
                    location = getPoint(point.xValue, point.median, xAxis, yAxis, isInverted);
                    break;
                case 1:
                    location = getPoint(point.xValue, point.maximum, xAxis, yAxis, isInverted);
                    break;
                case 2:
                    location = getPoint(point.xValue, point.minimum, xAxis, yAxis, isInverted);
                    break;
                case 3:
                    location = getPoint(point.xValue, point.upperQuartile, xAxis, yAxis, isInverted);
                    break;
                case 4:
                    location = getPoint(point.xValue, point.lowerQuartile, xAxis, yAxis, isInverted);
                    break;
                default: {
                    location = getPoint(point.xValue, point.outliers[labelIndex - 5], xAxis, yAxis, isInverted);
                    this.markerHeight = series.marker.height / 2;
                    break;
                }
            }
            if (isInverted) {
                location.y = point.regions[0].y + (point.regions[0].height / 2);
            }
            else {
                location.x = point.regions[0].x + (point.regions[0].width / 2);
            }
        }
        else if (isInverted && series.type.indexOf('Stacking') > -1 && point.yValue === 0) {
            location = { x: labelRegion.x + labelRegion.width, y: labelRegion.y + (labelRegion.height) / 2 };
        }
        else if (labelIndex === 0 || labelIndex === 1) {
            location = new ChartLocation(point.symbolLocations[0].x, point.symbolLocations[0].y);
        }
        else if ((labelIndex === 2 || labelIndex === 3) && series.type === 'Candle') {
            location = new ChartLocation(point.symbolLocations[1].x, point.symbolLocations[1].y);
        }
        else if (isInverted) {
            location = { x: labelRegion.x + (labelRegion.width) / 2, y: labelRegion.y };
        }
        else {
            location = { x: labelRegion.x + labelRegion.width, y: labelRegion.y + (labelRegion.height) / 2 };
        }
        //Aligning the label at the beginning of the tick, when tick size is less than text size
        if (labelIndex > 1 && series.type === 'HiloOpenClose') {
            if (series.chart.requireInvertedAxis) {
                const height = labelRegion.height;
                location.y = labelRegion.y + height / 2 + 2 * (labelIndex === 2 ? 1 : -1);
            }
            else {
                const width = labelRegion.width;
                location.x = labelRegion.x + width / 2 + 2 * (labelIndex === 2 ? 1 : -1);
            }
        }
        return location;
    }
    calculateRectPosition(labelLocation, rect, isMinus, position, series, textSize, labelIndex, point) {
        if (series.chart.chartAreaType === 'PolarRadar') {
            return null;
        }
        const padding = 5;
        const margin = this.margin;
        const textLength = !this.inverted ? textSize.height : textSize.width;
        if (position === 'Bottom' && series.type === 'StackingColumn' && !this.inverted && rect.height < textSize.height) {
            this.extraSpace = this.borderWidth + ((Math.abs(rect.height - textSize.height / 2) < padding) ? 0 : padding);
        }
        else {
            this.extraSpace = this.borderWidth + textLength / 2 + (position !== 'Outer' && series.type.indexOf('Column') > -1 &&
                (Math.abs(rect.height - textSize.height) < padding) ? 0 : padding);
        }
        if (series.type === 'StackingColumn100' || series.type === 'StackingBar100') {
            position = (position === 'Outer') ? 'Top' : position;
        }
        else if (series.type.indexOf('Range') > -1) {
            position = (position === 'Outer' || position === 'Top') ? position : 'Auto';
        }
        else if (series.type === 'Waterfall') {
            position = position === 'Auto' ? 'Middle' : position;
        }
        switch (position) {
            case 'Bottom':
                labelLocation = !this.inverted ?
                    isMinus ? (labelLocation + (series.type === 'Waterfall' ? (-this.extraSpace - margin.top - this.markerHeight) : (-rect.height + this.extraSpace + margin.top))) :
                        (labelLocation + rect.height - this.extraSpace - margin.bottom) :
                    isMinus ? (labelLocation + (series.type === 'Waterfall' ? (+this.extraSpace + margin.left + this.markerHeight) : (+rect.width - this.extraSpace - margin.left))) :
                        (labelLocation - rect.width + this.extraSpace + margin.right);
                break;
            case 'Middle':
                labelLocation = !this.inverted ?
                    (isMinus ? labelLocation - (rect.height / 2) : labelLocation + (rect.height / 2)) :
                    (isMinus ? labelLocation + (rect.width / 2) : labelLocation - (rect.width / 2));
                break;
            case 'Auto':
                labelLocation = this.calculateRectActualPosition(labelLocation, rect, isMinus, series, textSize, labelIndex, point);
                break;
            default:
                this.extraSpace += this.errorHeight;
                labelLocation = this.calculateTopAndOuterPosition(labelLocation, rect, position, series, labelIndex, this.extraSpace, isMinus, point);
                break;
        }
        const check = !this.inverted ? (labelLocation < rect.y || labelLocation > rect.y + rect.height) :
            (labelLocation < rect.x || labelLocation > rect.x + rect.width);
        this.fontBackground = check ?
            (this.fontBackground === 'transparent' ? this.chartBackground : this.fontBackground)
            : this.fontBackground === 'transparent' ? (point.color || series.interior) : this.fontBackground;
        const seriesLength = series.chart.series.length;
        if (position === 'Outer' && (series.type.indexOf('Stacking') > -1) && ((seriesLength - 1) > series.index)) {
            let nextSeries;
            let nextSeriesPoint;
            for (let i = series.index + 1; i < seriesLength; i++) {
                nextSeries = series.chart.series[i];
                nextSeriesPoint = nextSeries.points[point.index];
                if ((nextSeries.type.indexOf('Stacking') > -1) && (nextSeries.type.indexOf('100') === -1)) {
                    this.fontBackground = (nextSeriesPoint && ((nextSeriesPoint.yValue < 0 && point.yValue < 0) ||
                        (nextSeriesPoint.yValue > 0 && point.yValue > 0))) ? (nextSeriesPoint ? nextSeriesPoint.color :
                        nextSeries.interior) : this.fontBackground;
                    break;
                }
            }
        }
        return labelLocation;
    }
    calculatePathPosition(labelLocation, position, series, point, size, labelIndex) {
        const padding = 5;
        if ((series.type.indexOf('Area') > -1 && series.type !== 'RangeArea' && series.type !== 'SplineRangeArea' && series.type !== 'RangeStepArea')
            && this.yAxisInversed && series.marker.dataLabel.position !== 'Auto') {
            position = position === 'Top' ? 'Bottom' : position === 'Bottom' ? 'Top' : position;
        }
        this.fontBackground = this.fontBackground === 'transparent' ? this.chartBackground : this.fontBackground;
        switch (position) {
            case 'Top':
            case 'Outer':
                labelLocation = labelLocation - this.markerHeight - this.borderWidth - size.height / 2 - this.margin.bottom - padding -
                    this.errorHeight;
                break;
            case 'Bottom':
                labelLocation = labelLocation + this.markerHeight + this.borderWidth + size.height / 2 + this.margin.top + padding +
                    this.errorHeight;
                break;
            case 'Auto':
                labelLocation = this.calculatePathActualPosition(labelLocation, this.markerHeight, series, point, size, labelIndex);
                break;
        }
        return labelLocation;
    }
    isDataLabelShape(style) {
        this.isShape = (style.color !== 'transparent' || style.border.width > 0);
        this.borderWidth = style.border.width;
        if (!this.isShape) {
            this.margin = { left: 0, right: 0, bottom: 0, top: 0 };
        }
    }
    calculateRectActualPosition(labelLocation, rect, isMinus, series, size, labelIndex, point) {
        let location;
        let labelRect;
        let isOverLap = true;
        // Stacking series skip 'Outer' and begin automatic placement from 'Top'.
        let position = series.type.indexOf('Stacking') > -1 ? 1 : 0;
        const collection = this.chart.dataLabelCollections;
        const finalPosition = series.type.indexOf('Range') !== -1 || series.type === 'Hilo' ? 2 : 4;
        let actualPosition = this.getPosition(position);
        while (isOverLap && position < finalPosition) {
            actualPosition = this.getPosition(position);
            this.fontBackground = series.marker.dataLabel.fill;
            location = this.calculateRectPosition(labelLocation, rect, isMinus, actualPosition, series, size, labelIndex, point);
            if (!this.inverted) {
                labelRect = calculateRect(new ChartLocation(this.locationX, location), size, this.margin);
                isOverLap = labelRect.y < 0 || isCollide(labelRect, collection, series.clipRect) ||
                    labelRect.y > series.clipRect.height;
                if (series.marker.dataLabel.template === null && isOverLap !== true) {
                    isOverLap = labelRect.y / 2 + size.height + (point.regions[0].height - 2 * this.extraSpace) > series.clipRect.height;
                }
            }
            else {
                labelRect = calculateRect(new ChartLocation(location, this.locationY), size, this.margin);
                isOverLap = labelRect.x < 0 || isCollide(labelRect, collection, series.clipRect) ||
                    labelRect.x + labelRect.width > series.clipRect.width;
            }
            position++;
        }
        // Store the position resolved for the current rectangular-series label.
        this.actualPosition = actualPosition;
        return location;
    }
    // alignment calculation assigned here
    calculateAlignment(value, labelLocation, alignment, isMinus) {
        switch (alignment) {
            case 'Far':
                labelLocation = !this.inverted ? (isMinus ? labelLocation + value : labelLocation - value) :
                    (isMinus ? labelLocation - value : labelLocation + value);
                break;
            case 'Near':
                labelLocation = !this.inverted ? (isMinus ? labelLocation - value : labelLocation + value) :
                    (isMinus ? labelLocation + value : labelLocation - value);
                break;
        }
        return labelLocation;
    }
    //calculation for top and outer position of datalabel for rect series
    calculateTopAndOuterPosition(location, rect, position, series, index, extraSpace, isMinus, point) {
        const margin = this.margin;
        let top;
        switch (series.type) {
            case 'RangeColumn':
            case 'RangeArea':
            case 'RangeStepArea':
            case 'SplineRangeArea':
            case 'Hilo':
                top = (index === 0 && !this.yAxisInversed) || (index === 1 && this.yAxisInversed);
                location = this.updateLabelLocation(position, location, extraSpace, margin, rect, top);
                break;
            case 'Candle':
                top = (index === 0 || index === 2) && !this.yAxisInversed
                    || (index === 1 || index === 3) && this.yAxisInversed;
                location = this.updateLabelLocation(position, location, extraSpace, margin, rect, top, index > 1);
                break;
            case 'HiloOpenClose':
                if (index <= 1) {
                    top = (index === 0 && !this.yAxisInversed) || (index === 1 && this.yAxisInversed);
                    location = this.updateLabelLocation(position, location, extraSpace, margin, rect, top);
                }
                else {
                    if (this.yAxisInversed) {
                        location = !this.inverted ? location + extraSpace + margin.top : location - extraSpace - margin.right;
                    }
                    else {
                        location = !this.inverted ? location - extraSpace - margin.bottom : location + extraSpace + margin.left;
                    }
                }
                break;
            default:
                if (((isMinus && position === 'Top') || (!isMinus && position === 'Outer')) || (position === 'Top' && series.visiblePoints[point.index].yValue === 0)) {
                    location = !this.inverted ? location + (isMinus && series.type === 'Waterfall' ? (-rect.height + extraSpace + margin.bottom) : (-extraSpace - margin.bottom - this.markerHeight)) :
                        location + (isMinus && series.type === 'Waterfall' ? (+rect.width - extraSpace - margin.left) : (+extraSpace + margin.left + this.markerHeight));
                }
                else {
                    location = !this.inverted ? location + (isMinus && series.type === 'Waterfall' ? (-rect.height - extraSpace - margin.top) : (+extraSpace + margin.top + this.markerHeight)) :
                        location + (isMinus && series.type === 'Waterfall' ? (+rect.width + extraSpace + margin.top) : (-extraSpace - margin.right - this.markerHeight));
                }
                break;
        }
        return location;
    }
    /**
     * Updates the location of the data label.
     *
     * @param {LabelPosition} position - The position of the data label.
     * @param {number} location - The initial location of the data label.
     * @param {number} extraSpace - Extra space to adjust the label position.
     * @param {MarginModel} margin - The margin for the chart.
     * @param {Rect} rect - The rectangle associated with the data label.
     * @param {boolean} top - Indicates whether the label is positioned at the top.
     * @param {boolean} inside - Indicates whether the label is inside the chart area.
     * @returns {number} The updated location of the data label.
     */
    updateLabelLocation(position, location, extraSpace, margin, rect, top, inside = false) {
        if (!this.inverted) {
            if (top) {
                location = (position === 'Outer' && !inside) ? location - extraSpace - margin.bottom - this.markerHeight :
                    location + extraSpace + margin.top + this.markerHeight;
            }
            else {
                location = (position === 'Outer' && !inside) ? location + rect.height + extraSpace + margin.top + this.markerHeight :
                    location + rect.height - extraSpace - margin.bottom - this.markerHeight;
            }
        }
        else {
            if (top) {
                location = (position === 'Outer' && !inside) ? location + extraSpace + margin.left + this.markerHeight :
                    location - extraSpace - margin.right - this.markerHeight;
            }
            else {
                location = (position === 'Outer' && !inside) ? location - rect.width - extraSpace - margin.right - this.markerHeight :
                    location - rect.width + extraSpace + margin.left + this.markerHeight;
            }
        }
        return location;
    }
    calculatePathActualPosition(y, markerSize, series, point, size, labelIndex) {
        const points = series.points;
        const index = point.index;
        const yValue = points[index].yValue;
        let position;
        const nextPoint = points.length - 1 > index ? points[index + 1] : null;
        const previousPoint = index > 0 ? points[index - 1] : null;
        let yLocation;
        let isOverLap = true;
        let labelRect;
        let isBottom;
        let positionIndex;
        const collection = this.chart.dataLabelCollections;
        if (series.type === 'Bubble') {
            position = 'Top';
        }
        else if (series.type.indexOf('Step') > -1) {
            position = 'Top';
            if (index) {
                position = (!previousPoint || !previousPoint.visible || (yValue > previousPoint.yValue !== this.yAxisInversed)
                    || yValue === previousPoint.yValue) ? 'Top' : 'Bottom';
            }
        }
        else if (series.type === 'BoxAndWhisker') {
            if (labelIndex === 1 || labelIndex === 3 || labelIndex > 4) {
                position = series.yAxis.isAxisInverse ? 'Bottom' : 'Top';
            }
            else if (labelIndex === 2 || labelIndex === 4) {
                position = series.yAxis.isAxisInverse ? 'Top' : 'Bottom';
            }
            else {
                isOverLap = false;
                position = 'Middle';
                yLocation = this.calculatePathPosition(y, position, series, point, size, labelIndex);
            }
        }
        else {
            if (index === 0) {
                position = (!nextPoint || !nextPoint.visible || yValue > nextPoint.yValue ||
                    (yValue < nextPoint.yValue && this.yAxisInversed)) ? 'Top' : 'Bottom';
            }
            else if (index === points.length - 1) {
                position = (!previousPoint || !previousPoint.visible || yValue > previousPoint.yValue ||
                    (yValue < previousPoint.yValue && this.yAxisInversed)) ? 'Top' : 'Bottom';
            }
            else {
                if (!nextPoint.visible && !(previousPoint && previousPoint.visible)) {
                    position = 'Top';
                }
                else if (!nextPoint.visible || !previousPoint) {
                    position = (nextPoint.yValue > yValue || (previousPoint && previousPoint.yValue > yValue)) ?
                        'Bottom' : 'Top';
                }
                else {
                    const slope = (nextPoint.yValue - previousPoint.yValue) / 2;
                    const intersectY = (slope * index) + (nextPoint.yValue - (slope * (index + 1)));
                    position = !this.yAxisInversed ? intersectY < yValue ? 'Top' : 'Bottom' :
                        intersectY < yValue ? 'Bottom' : 'Top';
                }
            }
        }
        isBottom = position === 'Bottom';
        positionIndex = ['Outer', 'Top', 'Bottom', 'Middle', 'Auto'].indexOf(position);
        let actualPosition = position;
        while (isOverLap && positionIndex < 4) {
            actualPosition = this.getPosition(positionIndex);
            yLocation = this.calculatePathPosition(y, actualPosition, series, point, size, labelIndex);
            labelRect = calculateRect(new ChartLocation(this.locationX, yLocation), size, this.margin);
            isOverLap = labelRect.y < 0 || isCollide(labelRect, collection, series.clipRect)
                || (labelRect.y + labelRect.height) > series.clipRect.height;
            positionIndex = isBottom ? positionIndex - 1 : positionIndex + 1;
            isBottom = false;
        }
        // For path series, update the resolved position only when it is not Auto.
        if (actualPosition !== 'Auto') {
            this.actualPosition = actualPosition;
        }
        return yLocation;
    }
    /**
     * Initiates the animation for data labels.
     *
     * @param {Series} series - The series associated with the data labels.
     * @param {Element} [element] - The element to animate.
     * @returns {void}
     * @private
     */
    doDataLabelAnimation(series, element) {
        const shapeElements = series.shapeElement.childNodes;
        const textNode = series.textElement.childNodes;
        const delay = series.animation.delay + series.animation.duration;
        const duration = series.chart.animated ? series.chart.duration : 200;
        let location;
        const length = element ? 1 : textNode.length;
        let tempElement;
        for (let i = 0; i < length; i++) {
            tempElement = textNode[i];
            if (element) {
                element.style.visibility = 'hidden';
                templateAnimate(element, delay, duration, 'ZoomIn');
            }
            else {
                location = new ChartLocation((+tempElement.getAttribute('x')) + ((+tempElement.getAttribute('width')) / 2), (+tempElement.getAttribute('y')) + ((+tempElement.getAttribute('height')) / 2));
                markerAnimate(tempElement, delay, series.animation.duration, series, null, location, true);
                if (shapeElements[i]) {
                    tempElement = shapeElements[i];
                    location = new ChartLocation((+tempElement.getAttribute('x')) + ((+tempElement.getAttribute('width')) / 2), (+tempElement.getAttribute('y')) + ((+tempElement.getAttribute('height')) / 2));
                    markerAnimate(tempElement, delay, series.animation.duration, series, null, location, true);
                }
            }
        }
    }
    getPosition(index) {
        return (['Outer', 'Top', 'Bottom', 'Middle', 'Auto'][index]);
    }
    /**
     * Get module name.
     *
     * @returns {string} - Returns the module name.
     */
    getModuleName() {
        // Returns the module name
        return 'DataLabel';
    }
    /**
     * To destroy the dataLabel for series.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        // Destroy method performed here
    }
}
